import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import BrandLogo from '../components/BrandLogo';

export default function ForgotPasswordPage() {
  const { API } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to send reset link. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <BrandLogo size="lg" />
          <span className="text-lg font-semibold text-zinc-900 tracking-tight">Inspiro Projects</span>
        </div>
        <Card className="border-0 shadow-xl shadow-indigo-100/60 bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-semibold tracking-tight">{sent ? 'Check your email' : 'Forgot password?'}</CardTitle>
            <CardDescription className="text-zinc-500 text-sm">
              {sent ? `We sent a password reset link to ${email}. The link expires in 1 hour.`
                    : "Enter your email and we'll send you a link to reset your password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <div className="flex justify-center py-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle weight="fill" className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>
                <p className="text-sm text-zinc-500 text-center">
                  Didn't get the email?{' '}
                  <button onClick={() => setSent(false)} className="text-indigo-600 hover:text-indigo-700 font-medium">Try again</button>
                </p>
                <Link to="/login"><Button variant="outline" className="w-full h-10"><ArrowLeft className="w-4 h-4 mr-2" />Back to login</Button></Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Email</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)}
                      className="pl-10 h-10" required data-testid="forgot-password-email-input" />
                  </div>
                </div>
                {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">{error}</p>}
                <Button type="submit" className="w-full h-10 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium"
                  disabled={loading} data-testid="forgot-password-submit-btn">
                  {loading ? 'Sending…' : 'Send reset link'}
                </Button>
                <Link to="/login"><Button type="button" variant="ghost" className="w-full h-10 text-zinc-600"><ArrowLeft className="w-4 h-4 mr-2" />Back to login</Button></Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
